import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  Bars3Icon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { faqApi } from "../api/cmsApi";
import toast from "react-hot-toast";

interface FAQItem {
  _id: string;
  category: string;
  question: string;
  answer: string;
  order: number;
  isActive: boolean;
}

// Category keys (stored in DB) mapped to German display names
const categoryConfig: { key: string; label: string }[] = [
  { key: "general", label: "Allgemein" },
  { key: "eligibility", label: "Berechtigung & Anspruch" },
  { key: "documents", label: "Dokumente & Nachweise" },
  { key: "process", label: "Ablauf & Prozess" },
  { key: "payment", label: "Zahlung & Auszahlung" },
  { key: "legal", label: "Rechtliches & Datenschutz" },
  { key: "privacy", label: "Datenschutz & Privatsphäre" },
  { key: "security", label: "Sicherheit" },
  { key: "other", label: "Sonstiges" },
];

// Helper to get label from key
const getCategoryLabel = (key: string): string => {
  const cat = categoryConfig.find((c) => c.key === key);
  return cat ? cat.label : key;
};

export default function FaqManager() {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [localItems, setLocalItems] = useState<FAQItem[]>([]);
  const [dragState, setDragState] = useState<{
    id: string | null;
    category: string | null;
    overId: string | null;
  }>({ id: null, category: null, overId: null });

  const [formData, setFormData] = useState({
    category: categoryConfig[0].key,
    question: "",
    answer: "",
  });

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["faq"],
    queryFn: () => faqApi.list().then((res) => res.data.data || []),
  });

  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => faqApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq"] });
      setIsCreating(false);
      resetForm();
      toast.success("FAQ erstellt");
    },
    onError: () => toast.error("Fehler beim Erstellen"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<FAQItem> }) =>
      faqApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq"] });
      setEditingId(null);
      resetForm();
      toast.success("FAQ aktualisiert");
    },
    onError: () => toast.error("Fehler beim Aktualisieren"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => faqApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq"] });
      toast.success("FAQ gelöscht");
    },
    onError: () => toast.error("Fehler beim Löschen"),
  });

  const reorderMutation = useMutation({
    mutationFn: (payload: { id: string; order: number }[]) => faqApi.reorder(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faq"] });
      toast.success("Reihenfolge gespeichert");
    },
    onError: () => {
      setLocalItems(items);
      toast.error("Fehler beim Sortieren");
    },
  });

  const resetForm = () => {
    setFormData({
      category: selectedCategory || categoryConfig[0].key,
      question: "",
      answer: "",
    });
  };

  const startEditing = (item: FAQItem) => {
    setEditingId(item._id);
    setFormData({
      category: item.category,
      question: item.question,
      answer: item.answer,
    });
  };

  const handleToggleActive = (item: FAQItem) => {
    updateMutation.mutate({
      id: item._id,
      data: { isActive: !item.isActive },
    });
  };

  const handleReorder = (category: string, draggedId: string, targetId: string) => {
    setLocalItems((prev) => {
      const categoryItems = prev.filter((item) => item.category === category);
      const draggedIndex = categoryItems.findIndex((item) => item._id === draggedId);
      const targetIndex = categoryItems.findIndex((item) => item._id === targetId);

      if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
        return prev;
      }

      const reordered = [...categoryItems];
      const [moved] = reordered.splice(draggedIndex, 1);
      reordered.splice(targetIndex, 0, moved);

      const updatedCategoryItems = reordered.map((item, index) => ({
        ...item,
        order: index,
      }));

      const firstIndex = prev.findIndex((item) => item.category === category);
      if (firstIndex === -1) {
        return prev;
      }

      const otherItems = prev.filter((item) => item.category !== category);
      const updatedItems = [
        ...otherItems.slice(0, firstIndex),
        ...updatedCategoryItems,
        ...otherItems.slice(firstIndex),
      ];

      reorderMutation.mutate(
        updatedCategoryItems.map((item) => ({ id: item._id, order: item.order }))
      );

      return updatedItems;
    });
  };

  // Group items by category
  const displayItems = localItems.length ? localItems : items;
  const categoryOrder = categoryConfig.map((cat) => cat.key);
  const allCategories = [...new Set(displayItems.map((item: FAQItem) => item.category))] as string[];
  const categories: string[] = [
    ...categoryOrder.filter((key) =>
      displayItems.some((item: FAQItem) => item.category === key)
    ),
    ...allCategories.filter(
      (key) => !categoryOrder.includes(key)
    ),
  ];
  const groupedItems: Record<string, FAQItem[]> = categories.reduce(
    (acc: Record<string, FAQItem[]>, category: string) => {
      acc[category] = displayItems
        .filter((item: FAQItem) => item.category === category)
        .sort((a: FAQItem, b: FAQItem) => (a.order ?? 0) - (b.order ?? 0));
      return acc;
    },
    {} as Record<string, FAQItem[]>
  );

  const filteredCategories = selectedCategory ? [selectedCategory] : categories;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <QuestionMarkCircleIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              FAQ Manager
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Verwalten Sie häufig gestellte Fragen und Antworten
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setIsCreating(true);
            resetForm();
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Neue Frage
        </button>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`
            px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
            ${
              selectedCategory === null
                ? "bg-indigo-600 text-white dark:bg-indigo-500"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
            }
          `}
        >
          Alle
        </button>
        {categoryConfig.map((cat) => (
          <button
            key={cat.key}
            onClick={() => setSelectedCategory(cat.key)}
            className={`
              px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
              ${
                selectedCategory === cat.key
                  ? "bg-indigo-600 text-white dark:bg-indigo-500"
                  : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
              }
            `}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Neue FAQ hinzufügen
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Kategorie
              </label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                {categoryConfig.map((cat) => (
                  <option key={cat.key} value={cat.key}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Frage
              </label>
              <input
                type="text"
                value={formData.question}
                onChange={(e) =>
                  setFormData({ ...formData, question: e.target.value })
                }
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Was möchten Kunden wissen?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Antwort
              </label>
              <textarea
                value={formData.answer}
                onChange={(e) =>
                  setFormData({ ...formData, answer: e.target.value })
                }
                className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                rows={4}
                placeholder="Die ausführliche Antwort..."
              />
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setIsCreating(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Abbrechen
              </button>
              <button
                onClick={() => createMutation.mutate(formData)}
                disabled={createMutation.isPending}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
              >
                {createMutation.isPending ? "Erstellen..." : "Erstellen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FAQ List by Category */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          Laden...
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Keine FAQ-Einträge vorhanden.
          </p>
          <button
            onClick={() => setIsCreating(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            Erste Frage hinzufügen
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredCategories.map((category) => (
            <div
              key={category}
              className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden"
            >
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {getCategoryLabel(category)}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {groupedItems[category]?.length || 0} Fragen
                </p>
              </div>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {(groupedItems[category] || []).map((item: FAQItem) => {
                  const isDragging = dragState.id === item._id;
                  const isDropTarget =
                    dragState.overId === item._id && dragState.category === category;

                  return (
                    <div key={item._id}>
                    {editingId === item._id ? (
                      <div className="p-4 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Kategorie
                          </label>
                          <select
                            value={formData.category}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                category: e.target.value,
                              })
                            }
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          >
                            {categoryConfig.map((cat) => (
                              <option key={cat.key} value={cat.key}>
                                {cat.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Frage
                          </label>
                          <input
                            type="text"
                            value={formData.question}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                question: e.target.value,
                              })
                            }
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Antwort
                          </label>
                          <textarea
                            value={formData.answer}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                answer: e.target.value,
                              })
                            }
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            rows={4}
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => setEditingId(null)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                          >
                            <XMarkIcon className="h-4 w-4 mr-1" />
                            Abbrechen
                          </button>
                          <button
                            onClick={() =>
                              updateMutation.mutate({
                                id: item._id,
                                data: formData,
                              })
                            }
                            disabled={updateMutation.isPending}
                            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50"
                          >
                            <CheckIcon className="h-4 w-4 mr-1" />
                            Speichern
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`p-4 flex items-start gap-3 transition-colors ${
                          isDropTarget ? "bg-indigo-50 dark:bg-indigo-900/20" : ""
                        } ${isDragging ? "opacity-60" : ""}`}
                        onDragOver={(event) => {
                          if (!dragState.id || dragState.category !== category) return;
                          if (dragState.id === item._id) return;
                          event.preventDefault();
                          if (dragState.overId !== item._id) {
                            setDragState((prev) => ({ ...prev, overId: item._id }));
                          }
                        }}
                        onDragLeave={() => {
                          if (dragState.overId === item._id) {
                            setDragState((prev) => ({ ...prev, overId: null }));
                          }
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          if (!dragState.id || dragState.category !== category) return;
                          handleReorder(category, dragState.id, item._id);
                          setDragState({ id: null, category: null, overId: null });
                        }}
                      >
                        <div
                          className="text-gray-300 dark:text-gray-600 cursor-grab"
                          draggable
                          onDragStart={(event) => {
                            event.dataTransfer.effectAllowed = "move";
                            event.dataTransfer.setData("text/plain", item._id);
                            setDragState({ id: item._id, category, overId: null });
                          }}
                          onDragEnd={() => {
                            setDragState({ id: null, category: null, overId: null });
                          }}
                        >
                          <Bars3Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {item.question}
                            </h4>
                            <button
                              onClick={() => handleToggleActive(item)}
                              className={`
                                px-2 py-0.5 rounded-full text-xs font-medium
                                ${
                                  item.isActive
                                    ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                                }
                              `}
                            >
                              {item.isActive ? "Aktiv" : "Inaktiv"}
                            </button>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                            {item.answer}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => startEditing(item)}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              if (confirm("FAQ wirklich löschen?")) {
                                deleteMutation.mutate(item._id);
                              }
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
